import { useState, useEffect, useCallback } from "react";
import { Phone, CalendarClock, CheckCircle2, Trash2, Plus, Loader2, ClipboardList } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { listActivities, createActivity, updateActivity, deleteActivity } from "@/lib/api";
import { toast } from "sonner";

const TIPI = [["chiamata", "Chiamata"], ["appuntamento", "Appuntamento"], ["follow-up", "Follow-up"], ["email", "Email"], ["nota", "Nota"]];
export const ESITI = ["risposto", "non risposto", "richiamare", "appuntamento fissato", "interessato", "non interessato", "documenti richiesti", "pratica avviata"];
const ICON = { chiamata: Phone, appuntamento: CalendarClock, "follow-up": ClipboardList, email: ClipboardList, nota: ClipboardList };

const localNow = () => {
  const d = new Date(); d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
};
export const fmtDt = (iso) => iso ? new Date(iso).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "";

export default function LeadActivities({ leadId, onChanged }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({ tipo: "chiamata", data: localNow(), esito: "", note: "", done: true });
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(() => listActivities(leadId).then(setItems).catch(() => {}), [leadId]);
  useEffect(() => { refresh(); }, [refresh]);

  const add = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await createActivity(leadId, { ...form, data: new Date(form.data).toISOString(), esito: form.esito === "-" ? "" : form.esito });
      toast.success(form.done ? "Attività registrata" : "Follow-up programmato");
      setForm({ tipo: "chiamata", data: localNow(), esito: "", note: "", done: true });
      refresh(); onChanged?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Errore");
    } finally { setBusy(false); }
  };

  const toggle = async (a) => { await updateActivity(a.id, { done: !a.done }); refresh(); onChanged?.(); };
  const remove = async (a) => { await deleteActivity(a.id); refresh(); onChanged?.(); };

  return (
    <div data-testid="lead-activities">
      <div className="text-xs font-bold uppercase text-slate-400 mb-2 flex items-center gap-1"><Phone className="h-3.5 w-3.5" /> Attività e follow-up</div>
      <form onSubmit={add} className="rounded-lg bg-slate-900 border border-white/10 p-3 space-y-2">
        <div className="grid grid-cols-2 gap-2">
          <Select value={form.tipo} onValueChange={(v) => setForm({ ...form, tipo: v })}>
            <SelectTrigger data-testid="activity-tipo" className="bg-slate-950 border-white/10 text-white h-9"><SelectValue /></SelectTrigger>
            <SelectContent>{TIPI.map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
          </Select>
          <Input data-testid="activity-data" type="datetime-local" value={form.data} onChange={(e) => setForm({ ...form, data: e.target.value })}
            className="bg-slate-950 border-white/10 text-white h-9" />
          <Select value={form.esito || "-"} onValueChange={(v) => setForm({ ...form, esito: v })}>
            <SelectTrigger data-testid="activity-esito" className="bg-slate-950 border-white/10 text-white h-9"><SelectValue placeholder="Esito" /></SelectTrigger>
            <SelectContent><SelectItem value="-">Nessun esito</SelectItem>{ESITI.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
          </Select>
          <label className="flex items-center gap-2 text-xs text-slate-300 px-1">
            <input data-testid="activity-done" type="checkbox" checked={form.done} onChange={(e) => setForm({ ...form, done: e.target.checked })} className="accent-accent" />
            {form.done ? "Già svolta" : "Da fare (follow-up)"}
          </label>
        </div>
        <Textarea data-testid="activity-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Note sulla chiamata / appuntamento..."
          className="bg-slate-950 border-white/10 text-white min-h-16" />
        <Button data-testid="activity-save" type="submit" size="sm" disabled={busy} className="gap-2 bg-accent hover:bg-accent/90 text-white">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Registra
        </Button>
      </form>

      <ul className="mt-3 space-y-2">
        {items.map((a) => {
          const Icon = ICON[a.tipo] || ClipboardList;
          const overdue = !a.done && new Date(a.data) < new Date();
          return (
            <li key={a.id} data-testid={`activity-row-${a.id}`} className={`flex items-start gap-2 rounded-lg border px-3 py-2 text-sm ${overdue ? "border-rose-400/40 bg-rose-400/5" : "border-white/10 bg-slate-900"}`}>
              <button data-testid={`activity-toggle-${a.id}`} onClick={() => toggle(a)} title={a.done ? "Segna da fare" : "Segna come fatta"}
                className={`mt-0.5 ${a.done ? "text-emerald-400" : "text-slate-500 hover:text-emerald-400"}`}><CheckCircle2 className="h-4 w-4" /></button>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 text-white"><Icon className="h-3.5 w-3.5 text-accent" /> {a.titolo}
                  {a.esito && <span className="text-[10px] uppercase font-bold px-1.5 py-0.5 rounded bg-slate-800 text-slate-300 border border-white/10">{a.esito}</span>}
                </div>
                <div className={`text-xs ${overdue ? "text-rose-400" : "text-slate-500"}`}>{fmtDt(a.data)} {overdue && "· in ritardo"} {a.done && "· fatta"}</div>
                {a.note && <div className="text-xs text-slate-400 mt-1 whitespace-pre-wrap">{a.note}</div>}
              </div>
              <button data-testid={`activity-delete-${a.id}`} onClick={() => remove(a)} className="text-slate-600 hover:text-rose-400"><Trash2 className="h-4 w-4" /></button>
            </li>
          );
        })}
        {items.length === 0 && <li className="text-xs text-slate-500">Nessuna attività registrata.</li>}
      </ul>
    </div>
  );
}
